export const GAME_WIDTH = 1280;
export const GAME_HEIGHT = 720;

export const TOP_BAR_HEIGHT = 40;
export const BOTTOM_BAR_HEIGHT = 80;

export const TILE_SIZE = 64;
export const GRID_COLS = 20;
export const GRID_ROWS = 9;          // fits between top + bottom UI bars
export const MAP_OFFSET_X = 0;
export const MAP_OFFSET_Y = TOP_BAR_HEIGHT + 12;

export const STARTING_GOLD = 200;
export const STARTING_LIVES = 20;

/**
 * Texture-key suffixes for an enemy's walk-cycle frames, in cycle order:
 * `enemy-goblin`, `enemy-goblin-b`, … A unit uses as many leading entries as
 * its drawer registered, so a two-frame enemy and a six-frame one share the
 * same naming. generateTextures writes them, Enemy plays them, SpriteShower
 * lists them — keep this the only place the order is spelled out.
 */
export const WALK_FRAME_SUFFIXES = ["", "-b", "-c", "-d", "-e", "-f"] as const;

/**
 * Per-level tunables for a tower.
 *  - levels[0]      = build cost + base stats (level 1)
 *  - levels[i].cost = gold required to upgrade FROM level i TO level i+1
 *
 * Adding a new level is just appending an entry. Adding a new tower is
 * appending an entry to TOWERS — generateTextures.ts iterates levels for you.
 */
export interface TowerLevelStats {
  cost: number;
  range: number;
  fireRate: number;       // shots per second (or pulses/sec for aura)
  damage: number;
  projectileSpeed: number;
  // Optional behavior modifiers — absent = none.
  splashRadius?: number;        // AoE on impact
  slowFactor?: number;          // 0..1 multiplier (e.g. 0.5 = half speed)
  slowDurationMs?: number;
  dotDps?: number;              // damage per second from poison
  dotDurationMs?: number;
  pierceCount?: number;         // # of enemies a bolt passes through
  chainCount?: number;          // # of chain jumps
  chainRange?: number;          // jump distance
  auraFireRateBonus?: number;   // +x fire-rate granted to nearby towers
  auraRadius?: number;
}

export type TowerKey =
  | "archer" | "cannon" | "mage"
  | "frost" | "lightning" | "poison" | "crossbow" | "holy";

export type TowerCategory = "tower" | "wall" | "trap" | "support" | "hero";

export interface TowerStats {
  key: TowerKey;
  name: string;
  color: number;          // accent color (used for fallback texture & UI tint)
  description: string;
  /** Player Level required to build this tower. Defaults to 1 if omitted. */
  unlockLevel?: number;
  /** Defense category. Defaults to "tower" if omitted. */
  category?: TowerCategory;
  levels: TowerLevelStats[];
}

export const ENDLESS_TOWER_MAX_LEVEL = 12;

export const TOWERS: Record<string, TowerStats> = {
  archer: {
    key: "archer",
    name: "Archer",
    color: 0x8b6f3a,
    description: "Fast single-target arrows",
    unlockLevel: 1,
    levels: [
      { cost:  75, range: 180, fireRate: 1.6, damage: 12, projectileSpeed: 520 },
      { cost:  90, range: 210, fireRate: 2.0, damage: 18, projectileSpeed: 560 },
      { cost: 130, range: 240, fireRate: 2.5, damage: 28, projectileSpeed: 600 }
    ]
  },
  cannon: {
    key: "cannon",
    name: "Cannon",
    color: 0x3a3a3a,
    description: "Slow heavy hits",
    unlockLevel: 3,
    levels: [
      { cost: 150, range: 150, fireRate: 0.6, damage:  40, projectileSpeed: 360 },
      { cost: 180, range: 170, fireRate: 0.8, damage:  65, projectileSpeed: 400 },
      { cost: 240, range: 190, fireRate: 1.0, damage: 100, projectileSpeed: 440 }
    ]
  },
  mage: {
    key: "mage",
    name: "Mage",
    color: 0x5a3a8a,
    description: "Magical bolts",
    unlockLevel: 5,
    levels: [
      { cost: 200, range: 200, fireRate: 1.0, damage: 22, projectileSpeed: 600 },
      { cost: 220, range: 220, fireRate: 1.2, damage: 35, projectileSpeed: 640 },
      { cost: 300, range: 250, fireRate: 1.4, damage: 55, projectileSpeed: 700 }
    ]
  },
  frost: {
    key: "frost",
    name: "Frost Spire",
    color: 0x6cc7ff,
    description: "Slows + AoE chill",
    unlockLevel: 8,
    levels: [
      { cost: 175, range: 160, fireRate: 0.9, damage:  8, projectileSpeed: 420, splashRadius: 50, slowFactor: 0.55, slowDurationMs: 1500 },
      { cost: 200, range: 175, fireRate: 1.1, damage: 14, projectileSpeed: 460, splashRadius: 60, slowFactor: 0.45, slowDurationMs: 1800 },
      { cost: 260, range: 200, fireRate: 1.3, damage: 22, projectileSpeed: 500, splashRadius: 70, slowFactor: 0.35, slowDurationMs: 2200 }
    ]
  },
  lightning: {
    key: "lightning",
    name: "Tesla Coil",
    color: 0xffe070,
    description: "Chain lightning",
    unlockLevel: 12,
    levels: [
      { cost: 225, range: 180, fireRate: 0.8, damage: 18, projectileSpeed: 0, chainCount: 2, chainRange: 90 },
      { cost: 260, range: 200, fireRate: 1.0, damage: 28, projectileSpeed: 0, chainCount: 3, chainRange: 110 },
      { cost: 340, range: 220, fireRate: 1.2, damage: 42, projectileSpeed: 0, chainCount: 4, chainRange: 130 }
    ]
  },
  poison: {
    key: "poison",
    name: "Plague Hut",
    color: 0x6abf3a,
    description: "DoT splash",
    unlockLevel: 16,
    levels: [
      { cost: 180, range: 170, fireRate: 0.8, damage:  5, projectileSpeed: 380, splashRadius: 55, dotDps: 12, dotDurationMs: 3000 },
      { cost: 210, range: 185, fireRate: 1.0, damage:  9, projectileSpeed: 410, splashRadius: 65, dotDps: 20, dotDurationMs: 3500 },
      { cost: 280, range: 210, fireRate: 1.2, damage: 14, projectileSpeed: 440, splashRadius: 75, dotDps: 32, dotDurationMs: 4000 }
    ]
  },
  crossbow: {
    key: "crossbow",
    name: "Crossbow",
    color: 0x7a4a2a,
    description: "Pierces multiple foes",
    unlockLevel: 20,
    levels: [
      { cost: 160, range: 220, fireRate: 0.7, damage: 28, projectileSpeed: 720, pierceCount: 2 },
      { cost: 200, range: 250, fireRate: 0.9, damage: 42, projectileSpeed: 780, pierceCount: 3 },
      { cost: 270, range: 280, fireRate: 1.1, damage: 62, projectileSpeed: 840, pierceCount: 4 }
    ]
  },
  holy: {
    key: "holy",
    name: "Sanctum",
    color: 0xfff2a8,
    description: "Smite + speed aura",
    unlockLevel: 26,
    levels: [
      { cost: 250, range: 150, fireRate: 0.6, damage: 25, projectileSpeed: 0, auraFireRateBonus: 0.20, auraRadius: 130 },
      { cost: 300, range: 170, fireRate: 0.8, damage: 40, projectileSpeed: 0, auraFireRateBonus: 0.30, auraRadius: 150 },
      { cost: 380, range: 190, fireRate: 1.0, damage: 60, projectileSpeed: 0, auraFireRateBonus: 0.40, auraRadius: 170 }
    ]
  }
};

export function getTowerBuildCost(t: TowerStats): number {
  return t.levels[0].cost;
}

export function getTowerMaxLevel(t: TowerStats): number {
  return Math.max(t.levels.length, ENDLESS_TOWER_MAX_LEVEL);
}

/** Cost to upgrade FROM `currentLevel` TO `currentLevel + 1`, or null if maxed. */
export function getTowerUpgradeCost(t: TowerStats, currentLevel: number): number | null {
  const maxLevel = getTowerMaxLevel(t);
  if (currentLevel >= maxLevel) return null;
  if (currentLevel < t.levels.length) return t.levels[currentLevel].cost;

  const last = t.levels[t.levels.length - 1];
  const targetLevel = currentLevel + 1;
  const extraTier = targetLevel - t.levels.length;
  return Math.round(last.cost * Math.pow(2.15, extraTier) + currentLevel * 90);
}

export function getTowerLevelStats(t: TowerStats, level: number): TowerLevelStats {
  const maxLevel = getTowerMaxLevel(t);
  const clampedLevel = Math.max(1, Math.min(level, maxLevel));
  const idx = clampedLevel - 1;
  if (idx < t.levels.length) return t.levels[idx];

  const base = t.levels[t.levels.length - 1];
  const extraTier = clampedLevel - t.levels.length;
  const damageMult = 1 + extraTier * 0.42 + extraTier * extraTier * 0.035;
  const fireRateMult = 1 + extraTier * 0.09;
  const rangeBonus = extraTier * 14;
  const speedBonus = base.projectileSpeed > 0 ? extraTier * 24 : 0;
  return {
    ...base,
    cost: getTowerUpgradeCost(t, clampedLevel - 1) ?? base.cost,
    damage: Math.round(base.damage * damageMult),
    range: Math.round(base.range + rangeBonus),
    fireRate: Number((base.fireRate * fireRateMult).toFixed(2)),
    projectileSpeed: base.projectileSpeed + speedBonus,
    splashRadius: base.splashRadius == null ? undefined : Math.round(base.splashRadius + extraTier * 8),
    slowFactor: base.slowFactor == null ? undefined : Number(Math.max(0.25, base.slowFactor - extraTier * 0.025).toFixed(2)),
    slowDurationMs: base.slowDurationMs == null ? undefined : base.slowDurationMs + extraTier * 200,
    dotDps: base.dotDps == null ? undefined : Math.round(base.dotDps * damageMult),
    dotDurationMs: base.dotDurationMs == null ? undefined : base.dotDurationMs + extraTier * 250,
    pierceCount: base.pierceCount == null ? undefined : base.pierceCount + Math.floor((extraTier + 1) / 2),
    chainCount: base.chainCount == null ? undefined : base.chainCount + Math.floor((extraTier + 1) / 2),
    chainRange: base.chainRange == null ? undefined : base.chainRange + extraTier * 12,
    auraFireRateBonus: base.auraFireRateBonus == null ? undefined : Number((base.auraFireRateBonus + extraTier * 0.055).toFixed(2)),
    auraRadius: base.auraRadius == null ? undefined : base.auraRadius + extraTier * 14,
  };
}

/**
 * Largest level any tower currently defines. Used by generateTextures.ts so
 * we always pre-bake the full set of `tower-{key}-{lvl}` textures.
 * If you add a 4th level to *any* tower, this auto-bumps to 4.
 */
export const MAX_TOWER_LEVEL: number = ENDLESS_TOWER_MAX_LEVEL;

export interface EnemyStats {
  key: string;
  hp: number;
  speed: number;        // pixels per second
  reward: number;       // gold on kill
  damage: number;       // lives lost on leak
  color: number;
  radius: number;
  /** Optional resistances. 0=normal, 1=immune, fractional values reduce. */
  resist?: {
    slow?: number;
    poison?: number;
    pierce?: number;
    physical?: number;   // applied to archer/cannon/crossbow
    magic?: number;      // applied to mage/lightning/holy
  };
  /** Marks this entry as a boss for UI/save tracking. */
  boss?: boolean;
  /** Flying — only towers in `airTargetingTowers` set can hit. */
  flying?: boolean;
  /** Invisible until any tower comes within `invisRadius` px. */
  invisible?: boolean;
  invisRadius?: number;
  /** Chance (0..1) to dodge non-splash projectile hits. */
  dodgeChance?: number;
  /** Self-heal % of max HP per second while alive. */
  regenPctPerSec?: number;
  /** Revives once at this HP fraction (0..1) after a short delay. */
  reviveOncePct?: number;
  /** On death, spawn N enemies of `splitOnDeath.type`. */
  splitOnDeath?: { type: string; count: number };
}

export const ENEMIES: Record<string, EnemyStats> = {
  goblin:   { key: "goblin",   hp: 40,  speed: 90,  reward: 6,  damage: 1, color: 0x4a8a3a, radius: 10 },
  orc:      { key: "orc",      hp: 110, speed: 70,  reward: 12, damage: 2, color: 0x6a4a2a, radius: 14 },
  skeleton: { key: "skeleton", hp: 70,  speed: 110, reward: 9,  damage: 1, color: 0xcccccc, radius: 11 },
  troll:    { key: "troll",    hp: 320, speed: 55,  reward: 30, damage: 4, color: 0x8a3a3a, radius: 18 },
  // New variants — give the new towers reasons to exist.
  wraith:       { key: "wraith",       hp: 90,  speed: 130, reward: 14, damage: 1, color: 0xb088ff, radius: 12, resist: { slow: 1.0, physical: 0.5 } },
  armored_orc:  { key: "armored_orc",  hp: 220, speed: 60,  reward: 22, damage: 3, color: 0x707070, radius: 15, resist: { physical: 0.4, pierce: 0.3 } },
  swarmling:    { key: "swarmling",    hp: 18,  speed: 140, reward: 3,  damage: 1, color: 0xa0c060, radius: 7,  resist: {} },
  flame_imp:    { key: "flame_imp",    hp: 65,  speed: 100, reward: 11, damage: 1, color: 0xff6030, radius: 10, resist: { poison: 1.0, magic: 0.3 } },
  // -------- New regular enemies (P7) --------
  kobold_rider:    { key: "kobold_rider",    hp: 55,  speed: 130, reward: 10, damage: 1, color: 0x8a5a30, radius: 11, splitOnDeath: { type: "swarmling", count: 2 } },
  shieldman:       { key: "shieldman",       hp: 180, speed: 75,  reward: 16, damage: 2, color: 0x506080, radius: 13, resist: { physical: 0.6 } },
  bog_lurker:      { key: "bog_lurker",      hp: 120, speed: 90,  reward: 18, damage: 2, color: 0x305030, radius: 12, invisible: true, invisRadius: 110 },
  ice_elemental:   { key: "ice_elemental",   hp: 200, speed: 65,  reward: 22, damage: 2, color: 0x90c8e8, radius: 13, regenPctPerSec: 0.02, resist: { slow: 0.7 } },
  cart:            { key: "cart",            hp: 350, speed: 50,  reward: 28, damage: 3, color: 0x6a4a20, radius: 22 },
  desert_nomad:    { key: "desert_nomad",    hp: 130, speed: 105, reward: 18, damage: 1, color: 0xd0a060, radius: 11, dodgeChance: 0.30 },
  gargoyle:        { key: "gargoyle",        hp: 140, speed: 95,  reward: 20, damage: 2, color: 0x707080, radius: 12, flying: true, resist: { physical: 0.5 } },
  revenant:        { key: "revenant",        hp: 200, speed: 80,  reward: 26, damage: 2, color: 0x9080a0, radius: 13, reviveOncePct: 0.5 },
  shadow_assassin: { key: "shadow_assassin", hp: 110, speed: 120, reward: 24, damage: 2, color: 0x3a2a4a, radius: 11 },
  siege_ram:       { key: "siege_ram",       hp: 900, speed: 40,  reward: 60, damage: 5, color: 0x504030, radius: 20, resist: { pierce: 0.9, physical: 0.3 } },
  // -------- New regular enemies (P7.5) --------
  golem_guardian:  { key: "golem_guardian",  hp: 800, speed: 35,  reward: 55, damage: 4, color: 0x5e5e5e, radius: 18, resist: { physical: 0.6, pierce: 0.6, slow: 1.0 } },
  void_specter:    { key: "void_specter",    hp: 300, speed: 110, reward: 30, damage: 2, color: 0xa020f0, radius: 13, flying: true, resist: { magic: 0.5, physical: 0.3 }, dodgeChance: 0.40 },
  plague_carrier:  { key: "plague_carrier",  hp: 400, speed: 60,  reward: 28, damage: 2, color: 0x4b7f2b, radius: 14, resist: { poison: 1.0 }, splitOnDeath: { type: "swarmling", count: 3 } },
  elusive_stalker: { key: "elusive_stalker", hp: 150, speed: 140, reward: 25, damage: 2, color: 0x1f1f2e, radius: 10, invisible: true, invisRadius: 90, dodgeChance: 0.35 },
  hellhound:       { key: "hellhound",       hp: 240, speed: 130, reward: 24, damage: 2, color: 0xd84b20, radius: 12, reviveOncePct: 0.5 },
  // -------- BOSSES (placeholder stat blocks; full ability AI in Boss controller) --------
  boss_warlord:       { key: "boss_warlord",       hp: 4500,  speed: 50, reward: 400, damage: 8,  color: 0x8a2a2a, radius: 24, resist: { physical: 0.3 }, boss: true },
  boss_bridge_troll:  { key: "boss_bridge_troll",  hp: 7000,  speed: 45, reward: 500, damage: 10, color: 0x4a6a4a, radius: 26, resist: { physical: 0.4, slow: 0.5 }, boss: true },
  boss_frost_wyrm:    { key: "boss_frost_wyrm",    hp: 9000,  speed: 60, reward: 600, damage: 10, color: 0xa0d8ff, radius: 26, resist: { slow: 1.0, magic: 0.3 }, boss: true },
  // Campaign bosses for maps 5-9 (Dragon's Spine → Twilight Chasm). Scaling HP + biome-flavoured resists.
  boss_rock_wyrm:     { key: "boss_rock_wyrm",     hp: 11000, speed: 55, reward: 650, damage: 10, color: 0x9a5a30, radius: 26, resist: { physical: 0.4, pierce: 0.3 }, boss: true },
  boss_bog_giant:     { key: "boss_bog_giant",     hp: 13500, speed: 42, reward: 750, damage: 12, color: 0x3a5a34, radius: 28, resist: { poison: 1.0, slow: 0.6, physical: 0.25 }, regenPctPerSec: 0.015, boss: true },
  boss_sea_leviathan: { key: "boss_sea_leviathan", hp: 16000, speed: 58, reward: 850, damage: 12, color: 0x2a6a9a, radius: 28, resist: { magic: 0.4, slow: 0.4 }, boss: true },
  boss_siege_golem:   { key: "boss_siege_golem",   hp: 20000, speed: 40, reward: 1000, damage: 14, color: 0x707078, radius: 30, resist: { physical: 0.5, pierce: 0.6 }, boss: true },
  boss_shadow_titan:  { key: "boss_shadow_titan",  hp: 26000, speed: 55, reward: 1200, damage: 15, color: 0x3a2a4a, radius: 30, resist: { physical: 0.35, magic: 0.35, slow: 0.5 }, dodgeChance: 0.15, boss: true },
  // -------- New endgame bosses (P7.5) --------
  boss_abyssal_dread: { key: "boss_abyssal_dread", hp: 30000, speed: 42, reward: 1200, damage: 15, color: 0x2e0854, radius: 30, resist: { slow: 0.5, magic: 0.4 }, regenPctPerSec: 0.02, boss: true },
  boss_doom_bringer:  { key: "boss_doom_bringer",  hp: 38000, speed: 38, reward: 1500, damage: 20, color: 0x4a0e0e, radius: 32, resist: { physical: 0.4, pierce: 0.5 }, splitOnDeath: { type: "shieldman", count: 3 }, boss: true }
};

/**
 * Audio manifest. Single source of truth for every audio asset key + path.
 * Files live under public/audio/ and are loaded by BootScene. Missing files
 * are tolerated at runtime (Sound.ts skips unknown keys silently).
 *
 * Style: low-cortisol — calm acoustic medieval. See public/audio/CREDITS.md.
 */
export const AUDIO = {
  music: {
    // Each slot is an array of variant files. Sound.ts picks one at random
    // each time playMusic() is called so the same theme doesn't repeat.
    menu:      ["audio/music/tavern_1.mp3", "audio/music/tavern_2.mp3"],
    meadowA:   ["audio/music/meadow_a_1.mp3", "audio/music/meadow_a_2.mp3"],
    meadowB:   ["audio/music/meadow_b_1.mp3", "audio/music/meadow_b_2.mp3", "audio/music/meadow_b_3.mp3"],
    bossDrone: ["audio/music/boss_drone_1.mp3", "audio/music/boss_drone_2.mp3", "audio/music/boss_drone_3.mp3", "audio/music/boss_drone_4.mp3"],
    victory:   ["audio/music/victory_1.mp3"],
    defeat:    ["audio/music/defeat_1.mp3"]
  },
  ambience: {
    fire: ["audio/sfx/ambience/fire_crackle_loop.ogg"],
    wind: [
      "audio/sfx/ambience/wind_loop.ogg",
      "audio/sfx/ambience/wind_loop_alt1.ogg",
      "audio/sfx/ambience/wind_loop_alt2.ogg",
      "audio/sfx/ambience/wind_loop_alt3.ogg"
    ]
  },
  sfx: {
    tower: {
      archer:    "audio/sfx/tower/archer_shot.ogg",
      cannon:    "audio/sfx/tower/cannon_shot.ogg",
      mage:      "audio/sfx/tower/mage_shot.ogg",
      frost:     "audio/sfx/tower/frost_shot.ogg",
      lightning: "audio/sfx/tower/lightning_shot.ogg",
      poison:    "audio/sfx/tower/poison_shot.ogg",
      crossbow:  "audio/sfx/tower/crossbow_shot.ogg",
      holy:      "audio/sfx/tower/holy_shot.ogg"
    },
    enemy: {
      deathSoft:  "audio/sfx/enemy/death_soft.ogg",
      deathArmor: "audio/sfx/enemy/death_armor.ogg",
      deathBoss:  "audio/sfx/enemy/death_boss.ogg"
    },
    ui: {
      click:    "audio/sfx/ui/click.ogg",
      buy:      "audio/sfx/ui/buy.ogg",
      upgrade:  "audio/sfx/ui/upgrade.ogg",
      waveHorn: "audio/sfx/ui/wave_flute.ogg",
      bossHorn: "audio/sfx/ui/boss_horn.ogg"
    },
    ability: {
      rally:     "audio/sfx/ability/rally.ogg",
      meteor:    "audio/sfx/ability/meteor.ogg",
      heal:      "audio/sfx/ability/heal.ogg",
      conscript: "audio/sfx/ability/conscript.ogg"
    }
  }
} as const;

/** Default audio settings — calm by design. */
export const AUDIO_DEFAULTS = {
  master: 0.7,
  music:  0.4,
  sfx:    0.5,
  muted:  false,
  lateNight: false   // halves master when on
} as const;


